/**
 * The navigation model as pure data (PRD §5, T-31). No React, no i18n, no
 * icons, so it is unit-tested per role (src/utils/__tests__/navModel.test.ts).
 * `navigation.ts` adds the labels and icons.
 *
 * Visibility rules are exactly the pre-M3 `show` rules (= the route guards in
 * App.tsx); only the grouping, order and labels changed. The one removal:
 * "הלקוח שלי" (`/organizations/<own org>`) for staff, which pointed at the
 * employer org (still reachable from "לקוחות" and from search, PRD §5).
 *
 * Fix round (2026-10-07, owner decision: what the server refuses a role is
 * not shown to it): the customer_viewer has no internal screen at all (its
 * data is `/api/portal/*` only, so it lands in `/portal`), and the
 * customer_manager loses employees / portal access / activity log, whose APIs
 * need an OTE employee profile. `accessGrants.service.ts` on the server keeps
 * the same defaults (a test compares the two).
 */
export type NavRole = 'admin' | 'agent' | 'customer_manager' | 'customer_viewer' | undefined;

export type NavGroupId = 'today' | 'work' | 'customers' | 'sales' | 'time' | 'finance' | 'admin';

export interface NavModelItem {
  /** Stable id; also the `shell.nav.<id>` label key. */
  id: string;
  to: string;
}

export interface NavModelGroup {
  id: NavGroupId;
  /** `true` for the bottom-pinned, collapsible "ניהול והגדרות" (D2, Q-027). */
  pinned: boolean;
  /** C-08: a label key under `shell.navGroup.*` when it differs from the id (the employee's day-ordered groups). */
  labelKey?: string;
  items: NavModelItem[];
}

type Rule = (r: { admin: boolean; agent: boolean; manager: boolean; viewer: boolean; internal: boolean }) => boolean;

const all: Rule = () => true;
const internal: Rule = (r) => r.internal;
const staff: Rule = (r) => r.admin || r.agent;
const adminOnly: Rule = (r) => r.admin;
const adminManager: Rule = (r) => r.admin || r.manager;
const staffManager: Rule = (r) => r.admin || r.agent || r.manager;

interface Def { id: string; to: string; show: Rule }

const TREE: { id: NavGroupId; pinned?: boolean; items: Def[] }[] = [
  {
    id: 'today',
    items: [
      { id: 'today', to: '/', show: all },
    ],
  },
  {
    id: 'work',
    items: [
      { id: 'tickets', to: '/tickets', show: internal },
      // D-146 (Q-95) — "שיחות" became "דיונים" (the id stays: screen permissions are keyed by it).
      { id: 'conversations', to: '/discussions', show: internal },
      { id: 'tasks', to: '/tasks', show: staff },
      { id: 'projects', to: '/projects', show: staff },
      { id: 'milestones', to: '/milestones', show: staff },
      { id: 'meetings', to: '/meetings', show: staff },
      { id: 'voice', to: '/voice', show: staff },
    ],
  },
  {
    id: 'customers',
    items: [
      { id: 'organizations', to: '/organizations', show: internal },
      { id: 'contacts', to: '/contacts', show: staff },
      { id: 'engagements', to: '/engagements', show: staffManager },
      { id: 'documents', to: '/documents', show: staff },
      { id: 'deliveries', to: '/deliveries', show: staff },
      // FIX-D337 (D-337 §12): "חלוקות לאישור" - the team lead approves the task split (the page lists only hers).
      { id: 'taskSplits', to: '/task-splits', show: staff },
    ],
  },
  {
    id: 'sales',
    items: [
      { id: 'leads', to: '/leads', show: staff },
      { id: 'quotes', to: '/quotes', show: staff },
      // FIX-D337 (D-337 §2): "מסמכים לחתימה" - the OTE manager signs before a document goes to the customer.
      { id: 'documentsToSign', to: '/documents-to-sign', show: adminOnly },
      { id: 'products', to: '/products', show: adminOnly },
    ],
  },
  {
    id: 'time',
    items: [
      { id: 'attendance', to: '/attendance', show: staff },
      { id: 'workLogs', to: '/work-logs', show: internal },
      // D-319 sec.9: "פירוט לוגים" is the ONE page; "אישורים חודשיים" (D-70) is its "חוזי שירות" tab, no menu item of its own.
      // The server refuses anyone who sees only their own logs.
      { id: 'logDetail', to: '/log-detail', show: staff },
    ],
  },
  {
    id: 'finance',
    items: [
      { id: 'billing', to: '/billing', show: adminManager },
      { id: 'obligations', to: '/obligations', show: adminOnly },
      { id: 'cashFlow', to: '/cashflow', show: adminOnly },
      { id: 'reports', to: '/reports', show: internal },
    ],
  },
  {
    id: 'admin',
    pinned: true,
    items: [
      { id: 'employees', to: '/employee-profiles', show: adminOnly },
      { id: 'permissions', to: '/permissions', show: adminOnly },
      { id: 'portalAccess', to: '/portal-access', show: adminOnly },
      // FIX-BUGREPORTS-ACCESS: the OTE manager only by default (= the server catalog); a grant opens it.
      { id: 'bugReports', to: '/bug-reports', show: adminOnly },
      { id: 'integrations', to: '/integrations', show: staff },
      { id: 'systemSettings', to: '/settings/system', show: adminOnly },
      { id: 'emailTemplates', to: '/email-templates', show: adminOnly },
      { id: 'blocklist', to: '/blocklist', show: adminOnly },
      { id: 'api', to: '/api-platform', show: adminOnly },
      { id: 'backups', to: '/settings/backups', show: adminOnly },
      { id: 'auditTrail', to: '/settings/audit-trail', show: adminOnly },
      { id: 'diagnostics', to: '/settings/diagnostics', show: adminOnly },
    ],
  },
];

/**
 * C-08 / C-19 (clarity, D-10): the employee (`agent`) sees the SAME destinations
 * ordered by the day: my work, customers and projects, sales, meetings and
 * recordings, reports, and the pinned admin pair. Applied after the tree's
 * `show` / `allow` filter, so `navCatalog()` and the `screen:<id>` keys stay
 * exactly as they are; a tree item the layout does not name keeps its tree
 * group. Every other role keeps the tree order (D-10: employees only).
 */
const AGENT_LAYOUT: { id: NavGroupId; labelKey?: string; pinned?: boolean; items: string[] }[] = [
  { id: 'today', items: ['today'] },
  { id: 'work', labelKey: 'agent.work', items: ['tickets', 'conversations', 'tasks', 'workLogs', 'logDetail', 'attendance', 'taskSplits'] },
  { id: 'customers', labelKey: 'agent.customers', items: ['organizations', 'contacts', 'projects', 'milestones', 'engagements', 'documents', 'deliveries'] },
  { id: 'sales', items: ['leads', 'quotes'] },
  { id: 'time', labelKey: 'agent.time', items: ['meetings', 'voice'] },
  { id: 'finance', labelKey: 'agent.finance', items: ['reports'] },
  { id: 'admin', pinned: true, items: ['bugReports', 'integrations'] },
];

function regroupForAgent(groups: NavModelGroup[]): NavModelGroup[] {
  const visible = new Map<string, { item: NavModelItem; group: NavGroupId }>();
  for (const g of groups) for (const item of g.items) visible.set(item.id, { item, group: g.id });
  const placed = new Set<string>();
  const out: NavModelGroup[] = AGENT_LAYOUT.map((g) => ({
    id: g.id,
    pinned: !!g.pinned,
    ...(g.labelKey ? { labelKey: g.labelKey } : {}),
    items: g.items.filter((id) => visible.has(id)).map((id) => { placed.add(id); return visible.get(id)!.item; }),
  }));
  for (const g of groups) for (const item of g.items) if (!placed.has(item.id)) out.find((o) => o.id === g.id)?.items.push(item);
  return out.filter((g) => g.items.length > 0);
}

function roleFlags(role: NavRole) {
  return {
    admin: role === 'admin',
    agent: role === 'agent',
    manager: role === 'customer_manager',
    viewer: role === 'customer_viewer',
    internal: role !== 'customer_viewer',
  };
}

/** The four account roles, in the order screens and tests list them. */
export const NAV_ROLES = ['admin', 'agent', 'customer_manager', 'customer_viewer'] as const;

export interface NavCatalogItem {
  id: string;
  to: string;
  group: NavGroupId;
  /** The roles that see this destination by default (the `show` rule above). */
  defaultRoles: (typeof NAV_ROLES)[number][];
}

/**
 * Every destination in the tree, whatever the role, with the roles that see
 * it by default. The screen permissions (`screen:<id>`, `permissions/
 * screenAccess.ts`) are built from this list, so a new nav item is a new
 * screen key in one place.
 */
export function navCatalog(): NavCatalogItem[] {
  return TREE.flatMap((g) => g.items.map((i) => ({
    id: i.id,
    to: i.to,
    group: g.id,
    defaultRoles: NAV_ROLES.filter((role) => i.show(roleFlags(role))),
  })));
}

/**
 * The visible groups for a role (empty groups removed). With `allow`, the
 * caller's effective screens decide instead of the role rule (screen
 * permissions, `usePermissions`): a screen the person may not open is not in
 * the model at all, and a group left without items disappears.
 */
export function buildNavModel(role: NavRole, orgId?: string | null, allow?: (id: string) => boolean): NavModelGroup[] {
  const r = roleFlags(role);
  const groups = TREE.map((g) => ({
    id: g.id,
    pinned: !!g.pinned,
    items: g.items.filter((i) => (allow ? allow(i.id) : i.show(r))).map(({ id, to }) => ({ id, to })),
  }));
  // `orgId` is kept for callers: the portal identity's own customer page
  // ("הלקוח שלי") was dropped with its internal screens (fix round, 2026-10-07):
  // `/organizations/*` is refused to it (`requirePortalOnly`); the portal is its home.
  void orgId;
  const visible = groups.filter((g) => g.items.length > 0);
  return role === 'agent' ? regroupForAgent(visible) : visible;
}

/** Every route in the model for a role (flat), in nav order. */
export function navRoutes(role: NavRole, orgId?: string | null): string[] {
  return buildNavModel(role, orgId).flatMap((g) => g.items.map((i) => i.to));
}

function matches(pathname: string, path: string): boolean {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * The single active destination: the most specific (longest) matching path,
 * so `/organizations/<id>` never lights two items (PRD §4.2).
 */
export function activeNavPath(pathname: string, paths: string[]): string | null {
  let best: string | null = null;
  for (const p of paths) {
    if (matches(pathname, p) && (best === null || p.length > best.length)) best = p;
  }
  return best;
}

/** Back-compat helper: does `path` match `pathname` (segment-aware)? */
export function isNavMatch(pathname: string, path: string): boolean {
  return matches(pathname, path);
}
